import React, { useState, useRef, useEffect } from 'react';
import Tooltip from './Tooltip';
import DropdownMenu from './DropdownMenu';
import colors from '../../utils/colors';
import typography from '../../utils/typography';
import { TriangleDownIcon } from '../../utils/icons';

const MediumButton = ({
  children,
  onClick,
  className = "",
  isActive = false,
  variant = "default",
  text,
  tooltip,
  tooltipPosition = "top",
  tooltipDelay = 300,
  leftIcon,
  rightIcon,
  style = {}, // Allow custom style overrides
  // Dropdown props
  dropdownOptions = null, // Array of { value, label, icon?, disabled? }
  dropdownValue = null,
  onDropdownChange = null,
  dropdownPosition = "bottom-right",
  dropdownWidth = 180,
  showDropdownCheck = true,
  showSelectedLabel = false, // When true, shows selected option's label instead of custom text
  showDropdownIcon = true, // When false, hides the dropdown chevron icon
  // Multi-select props
  isMultiSelect = false,
  dropdownValues = [], // Array of selected values for multi-select
  onDropdownMultiChange = null // Callback for multi-select changes
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const tooltipTimeoutRef = useRef(null);
  const buttonRef = useRef(null);
  
  const isDropdown = Boolean(dropdownOptions && dropdownOptions.length > 0);
  const selectedOption = dropdownOptions?.find(opt => opt.value === dropdownValue);
  const selectedValues = isMultiSelect ? dropdownValues : (dropdownValue ? [dropdownValue] : []);
  const getButtonStyles = () => {
    switch (variant) {
      case "primary":
        return `hover:bg-opacity-90`;
      case "secondary":
        return `hover:bg-opacity-80`;
      case "tertiary":
        return `bg-transparent border hover:bg-white/5`;
      case "aura":
        return `hover:brightness-110`;
      default:
        return `hover:bg-opacity-80`;
    }
  };

  const isLabelButton = text && (variant === "primary" || variant === "secondary" || variant === "tertiary" || variant === "aura");
  const buttonSize = isLabelButton ? "h-10 px-4 gap-1" : "w-10 h-10";

  const getIconColor = () => {
    switch (variant) {
      case "primary":
        return colors.bg.primary || '#0E0808';
      case "secondary":
      case "tertiary":
      case "aura":
        return colors.text.primary || '#FAF7F5';
      default:
        return colors.text.primary || '#FAF7F5';
    }
  };

  const getBackgroundStyle = () => {
    const baseStyle = {};
    
    if (variant === "aura") {
      baseStyle.backgroundImage = "url('/aura.png')";
      baseStyle.backgroundSize = "cover";
      baseStyle.backgroundPosition = "center";
      baseStyle.backgroundRepeat = "no-repeat";
    }
    
    if (variant === "primary") {
      baseStyle.backgroundColor = colors.text.primary;
      baseStyle.color = colors.bg.primary;
    }

    if (variant === "secondary") {
      baseStyle.backgroundColor = colors.bg.fog_thin;
      baseStyle.color = colors.text.primary;
    }

    if (variant === "tertiary") {
      baseStyle.borderColor = colors.border.primary;
      baseStyle.color = colors.text.primary;
    }

    if (variant === "aura") {
      baseStyle.color = colors.text.primary;
    }
    
    // Merge custom style overrides (custom styles take precedence)
    return { ...baseStyle, ...style };
  };

  const handleMouseEnter = () => {
    if (tooltip) {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
      tooltipTimeoutRef.current = setTimeout(() => {
        setShowTooltip(true);
      }, tooltipDelay);
    }
  };

  const handleMouseLeave = () => {
    if (tooltip) {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
      setShowTooltip(false);
    }
  };


  const handleButtonClick = (e) => {
    if (isDropdown) {
      e.preventDefault();
      setIsDropdownOpen(!isDropdownOpen);
    } else if (onClick) {
      onClick(e);
    }
  };

  const handleDropdownClose = () => {
    setIsDropdownOpen(false);
  };

  return (
    <div className="relative" ref={buttonRef}>
      <div
        className={`flex justify-center items-center flex-grow-0 flex-shrink-0 rounded-[100px] cursor-pointer ${buttonSize} ${getButtonStyles()} ${className}`}
        style={getBackgroundStyle()}
        onClick={handleButtonClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Left Icon - User controlled or selected option fallback */}
        {leftIcon ? (
          <div className="w-4 h-4 flex items-center justify-center" style={{ color: getIconColor() }}>
            {leftIcon}
          </div>
        ) : isDropdown && selectedOption?.icon && !text && !rightIcon && !children ? (
          // Fallback: show selected option icon only if no other content
          <div className="w-4 h-4 flex items-center justify-center" style={{ color: getIconColor() }}>
            {selectedOption.icon}
          </div>
        ) : null}
        
        {children}
        
        {/* Text/Label - User controlled, selected option, or fallback */}
        {text && showSelectedLabel && isDropdown && selectedOption ? (
          // Show selected option's label when showSelectedLabel is true
          <p style={{ ...typography.xsmall.title }}>
            {selectedOption.label}
          </p>
        ) : text ? (
          // Show custom text
          <p style={{ ...typography.xsmall.title }}>
            {text}
          </p>
        ) : isDropdown && selectedOption && !leftIcon && !rightIcon && !children ? (
          // Fallback: show selected option label only if no other content
          <p style={{ ...typography.xsmall.title }}>
            {selectedOption.label}
          </p>
        ) : null}
        
        {/* Right Icon - User controlled or dropdown arrow */}
        {rightIcon ? (
          <div className="w-4 h-4 flex items-center justify-center" style={{ color: getIconColor() }}>
            {rightIcon}
          </div>
        ) : isDropdown && showDropdownIcon ? (
          <div className="w-4 h-4 flex items-center justify-center" style={{ color: getIconColor() }}>
            <TriangleDownIcon />
          </div>
        ) : null}
      </div>

      {/* Dropdown Menu */}
      <DropdownMenu
        isOpen={isDropdown && isDropdownOpen}
        onClose={handleDropdownClose}
        options={dropdownOptions}
        selectedValue={dropdownValue}
        selectedValues={selectedValues}
        onSelect={onDropdownChange}
        onMultiSelect={onDropdownMultiChange}
        isMultiSelect={isMultiSelect}
        position={dropdownPosition}
        width={dropdownWidth}
        showCheck={showDropdownCheck}
        anchorRef={buttonRef}
      />

      {tooltip && (
        <Tooltip 
          text={tooltip} 
          isVisible={showTooltip} 
          position={tooltipPosition} 
        />
      )}
    </div>
  );
};

export default MediumButton;